import { useForm, validator } from 'formoid';
import { FormEvent } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  PasswordField,
  PasswordTooltipContent,
  SmallPagePaper,
  Tooltip,
  Button,
  useModalContext,
} from '~/common/components';
import { customValidator, propagateBackendValidationErrors } from '~/common/utils';
import { useCreatePassword, useValidateToken } from './hooks';

type RouteParams = {
  token: string;
};

const initialValues = {
  password: '',
  confirmPassword: '',
};

export const CreatePassword = () => {
  const { params } = useRouteMatch<RouteParams>();

  const { mutateAsync } = useCreatePassword();

  const modals = useModalContext();

  const validate = useValidateToken(params.token);

  const { fieldProps, handleSubmit, setErrors, isSubmitting, errors } = useForm({
    initialValues,
    validationStrategy: 'onBlur',
    validators: ({ password }) => ({
      password: customValidator.password(),
      confirmPassword: validator.sequence(
        validator.fromPredicate(
          (confirmPassword) => confirmPassword === password,
          'Passwords should match',
        ),
        customValidator.password(),
      ),
    }),
  });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    handleSubmit((values) => {
      return mutateAsync({ token: params.token, ...values }).catch((error) => {
        propagateBackendValidationErrors({ error, setErrors, formErrors: errors });
      });
    });
  };

  // TODO use loader instead
  if (validate.isLoading) {
    return <div className="min-h-full" />;
  }

  return (
    <>
      <SmallPagePaper className="!pt-[122px] items-start">
        <h2 className="font-brand-h5 mb-3">Create password</h2>

        <form onSubmit={submit}>
          <Tooltip content={<PasswordTooltipContent />} placement="right">
            <PasswordField
              {...fieldProps('password')}
              title="Password"
              placeholder="Enter your password"
              className="mb-3"
            />
          </Tooltip>

          <PasswordField
            {...fieldProps('confirmPassword')}
            title="Confirm Password"
            placeholder="Enter your password"
            className="mb-4"
          />

          <Button
            className="w-full"
            size="m"
            color="secondary-navy"
            loading={isSubmitting}
            type="submit"
          >
            Create account
          </Button>
        </form>
      </SmallPagePaper>
    </>
  );
};
